import axios from 'axios'
import { ADMIN_API_URL } from 'const'

const BASE_URL = `${ADMIN_API_URL}/metrics/stream`

// Continuous-audio ingest latency, one row per UTC day, newest first.
//
// `days` is capped at 90 by the server, and by more than convention: span
// rows carry a 90-day TTL, so there is nothing older to read until the
// per-user-day rollup exists. Omit `userId` for the fleet view, which
// carries no user ids by design. The server shares one response across
// callers for 60s, so `as_of` in the payload is the number to show — not
// the moment the request returned.
// `sealCause` and `clientVersion` narrow every figure to one stratum. The
// cause vocabulary is open by design — a client may add one without a server
// release — so the values to offer come back in the response's `facets`
// rather than being listed here.
export const getLatency = async ({
  days,
  userId,
  sealCause,
  clientVersion,
} = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/latency`, {
      params: {
        days,
        ...(userId ? { user_id: userId } : {}),
        ...(sealCause ? { seal_cause: sealCause } : {}),
        ...(clientVersion ? { client_version: clientVersion } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// Capture health — how well the fleet is recording, which the latency figures
// above cannot see: a device recording 46 minutes of an hour seals every span.
// Same strata as `getLatency`, so a filter carries between the two sections and
// is looking at the same population. Fleet view carries no user ids by design;
// `fleet.silent` (live mic, nothing declared) is a count for that reason.
// `since` is the previous response's `pulse.at`: the pulse then counts what
// moved after it, so each refresh animates exactly one tick's worth of events.
export const getCapture = async ({
  days,
  userId,
  sealCause,
  clientVersion,
  since,
} = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/capture`, {
      params: {
        days,
        ...(userId ? { user_id: userId } : {}),
        ...(sealCause ? { seal_cause: sealCause } : {}),
        ...(clientVersion ? { client_version: clientVersion } : {}),
        ...(since ? { since } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One account's spans, newest first. `userId` is required — a fleet-wide span
// list is not a surface this design has; the aggregates above are the fleet's
// answer — and `me` is the signed-in staff account, resolved server-side. Page
// with `beforeStartMs` from the previous response's `next_before_start_ms`,
// which is the last row's own key, so paging cannot skip or repeat a span
// while new ones arrive.
export const getSpans = async ({
  userId = 'me',
  limit,
  beforeStartMs,
} = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/spans`, {
      params: {
        user_id: userId,
        ...(limit ? { limit } : {}),
        ...(beforeStartMs != null ? { before_start_ms: beforeStartMs } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// What the router did and what it cost — step 2a's pipeline health. Sums
// `routing.totals` over spans routed in the window: the speech fraction
// (content) and the routed fraction (spend) as separate rows, per-stage CPU
// cost with the rates' provenance, the unrouted backlog with its oldest age,
// and `classes` — every YAMNet class heard, ranked by minutes and named
// server-side. No seal-cause or client-build strata: routing is a property of
// the server pass, not of how the phone closed the span. Cached 60s.
export const getPipeline = async ({ days, userId } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/pipeline`, {
      params: { days, ...(userId ? { user_id: userId } : {}) },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// The Loop page's Pushes tab, its first half: how the pushes were delivered —
// each day's quote-push outcomes and the held friend pushes, from Mongo
// (reclip-api `stream/metrics/loop/delivery.py`).
export const getLoopDelivery = async ({ days } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/loop/delivery`, {
      params: { days },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// The Loop page's Listens and Friends tabs, from the stream event table in
// BigQuery (reclip-api `stream/metrics/loop/listens.py`, `friends.py`): kept
// five minutes server-side, and a 503 with the reason when the table cannot be
// read, which the error says.
const loopTab =
  (tab) =>
  async ({ days } = {}) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/loop/${tab}`, {
        params: { days },
      })
      return data
    } catch (err) {
      throw new Error(err.response?.data?.detail || err.message)
    }
  }
export const getLoopListens = loopTab('listens')
export const getLoopFriends = loopTab('friends')

// The Loop page's Pushes tab: each kind of push sent in the window and what
// it led to, read from the stream event table in BigQuery (reclip-api
// `stream/metrics/loop/pushes.py`). The server keeps one answer per window
// for five minutes. It answers 503 with the reason when the environment has
// no table or BigQuery refuses, never zeros, and the error says so.
export const getLoopPushes = async ({ days } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/loop/pushes`, {
      params: { days },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One account's routed spans, newest first — what the router heard. `userId`
// is required and `me` is the signed-in staff account, resolved server-side,
// so the page never needs to know its own id. The fleet's version is
// `getRoutingFleet`, which names no account. Page with `beforeStartMs` from
// the previous response's `next_before_start_ms`.
export const getRouting = async ({
  userId = 'me',
  limit,
  beforeStartMs,
} = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/routing`, {
      params: {
        user_id: userId,
        ...(limit ? { limit } : {}),
        ...(beforeStartMs != null ? { before_start_ms: beforeStartMs } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// Every account's routed spans, newest routed first, with no account on any
// row: each minute's decision, the speech and routed fractions, the found
// moments' windows and outcomes, and the span's cost. Page with `before` from
// the previous response's `next_before`.
export const getRoutingFleet = async ({ limit, before } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/routing/fleet`, {
      params: {
        ...(limit ? { limit } : {}),
        ...(before ? { before } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One span's forever object, per segment: VAD regions, the level gate's
// numbers, every class above the floor with its name, the media score and the
// timings. One GCS read per call, so it is fetched when a span is opened and
// never with the list.
export const getRoutingSpan = async ({ userId = 'me', spanStartMs }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/routing/span`, {
      params: { user_id: userId, span_start_ms: spanStartMs },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One received segment's audio, as a blob for an <audio> element. The bearer
// header rides on axios, which an <audio src> could not send, so the bytes are
// fetched and handed over as an object URL. Gated server-side by
// STREAM_ADMIN_PLAYBACK: `me` everywhere but develop, so a 403 here is the
// policy, not a bug.
export const getRoutingSegment = async ({
  userId = 'me',
  spanStartMs,
  name,
}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/routing/segment`, {
      params: { user_id: userId, span_start_ms: spanStartMs, name },
      responseType: 'blob',
    })
    return data
  } catch (err) {
    let detail = err.message
    // A blob response carries the JSON error as a blob too.
    if (err.response?.data instanceof Blob) {
      try {
        detail = JSON.parse(await err.response.data.text()).detail || detail
      } catch {
        // keep the transport message
      }
    } else if (err.response?.data?.detail) {
      detail = err.response.data.detail
    }
    throw new Error(detail)
  }
}

// Erase one span for good: its audio, its router record with every YAMNet
// class, and the span row. Nothing of it is left to read, and it leaves every
// figure on this page within a minute. The owning account only, server-side
// and with no setting that widens it — so on somebody else's span the answer
// is a 403 that says so, and that is the rule rather than a fault. Both
// buckets keep a 7-day soft delete, so a mistaken erase is operator-restorable
// for a week.
// What the engine heard: one span's turns, timed words and provenance
// (audio-streaming step 2b). The hot transcript while it lives, the forever
// objects past its 30 days. Gated by the same playback policy as segment audio
// — a transcript is the audio's content in words — so another account's is a
// 403 naming the policy, and a span with no transcript is a 404 naming why.
export const getRoutingTranscript = async ({ userId = 'me', spanStartMs }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/routing/transcript`, {
      params: { user_id: userId, span_start_ms: spanStartMs },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

export const deleteRoutingSpan = async ({ userId = 'me', spanStartMs }) => {
  try {
    const { data } = await axios.delete(`${BASE_URL}/routing/span`, {
      params: { user_id: userId, span_start_ms: spanStartMs },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// ---- Audio-streaming step 4a: moments --------------------------------------
//
// Every non-staff user's moments, newest first; on production other staff
// users' are excluded (the caller's own are not), exactly as the reclips
// search page does — on develop and staging staff see each other's. Never a
// deleted moment. Omit `userId` for every user; `me` is the caller. `state` is
// the review state, all | pending | kept | hidden; `media` asks the media rule
// whatever its mode, any | clean (what it would serve) | flagged (what it would
// hide); `q` searches the quote and the hook. Page with `before` from the
// previous response's `next_before`.
export const getMoments = async ({
  userId,
  state,
  media,
  q,
  fromMs,
  toMs,
  limit,
  before,
} = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/moments`, {
      params: {
        ...(userId ? { user_id: userId } : {}),
        ...(state && state !== 'all' ? { state } : {}),
        ...(media && media !== 'any' ? { media } : {}),
        ...(q ? { q } : {}),
        ...(fromMs != null ? { from_ms: fromMs } : {}),
        ...(toMs != null ? { to_ms: toMs } : {}),
        ...(limit ? { limit } : {}),
        ...(before ? { before } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One moment as the list serves it, for a span's ledger row to open its card.
export const getMoment = async ({ momentId }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/moments/${momentId}`)
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One moment's cut, as a blob for an <audio> element (the bearer header rides
// on axios, which an <audio src> could not send).
export const getMomentAudio = async ({ momentId }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/moments/${momentId}/audio`, {
      responseType: 'blob',
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// The `selection/` trace a moment was cut under: what the ladder saw and
// decided, at which settings.
export const getMomentTrace = async ({ momentId }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/moments/${momentId}/trace`)
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// The latest spans a selection job touched, newest first, with outcome tallies
// — where a span that produced nothing is found.
export const getSelectionSpans = async ({ userId, limit } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/selection/spans`, {
      params: {
        ...(userId ? { user_id: userId } : {}),
        ...(limit ? { limit } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One span's selection ledger: every moment the selector found, its quote,
// strength, the judge's wins/n and the outcome, with what the span cost (one
// row per task and billed unit), how many cards it made, and its times.
// Refused moments are rows only here.
export const getSelectionSpan = async ({ userId, spanStartMs }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/selection/span`, {
      params: { user_id: userId, span_start_ms: spanStartMs },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// A span's trace by key, for a span that produced no moment to link from.
export const getSelectionTrace = async ({ userId, spanStartMs }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/selection/trace`, {
      params: { user_id: userId, span_start_ms: spanStartMs },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One user's feed as their phone shows it: their released quotes and their
// friends' lane in one list, newest release first, as the moments list's
// cards. Reading it stamps nothing on the quotes. `userId` is required (`me`
// is the caller); page with `before` from `next_before`.
export const getFeed = async ({ userId, limit, before }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/feed`, {
      params: {
        user_id: userId,
        ...(limit ? { limit } : {}),
        ...(before ? { before } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// The Users page's list: every account holding a span row (90 days),
// freshest heartbeat first and never-beaten last, each with its microphone
// off the heartbeat lease and 30 days of audio streamed and moments cut. On
// production other staff are left off (`staff_excluded`). Page with `before`.
// `userId` narrows it to one account: its row while the list holds it, else
// none (the Chat page's link to the profile).
export const getUsers = async ({ userId, limit, before } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/users`, {
      params: {
        ...(userId ? { user_id: userId } : {}),
        ...(limit ? { limit } : {}),
        ...(before ? { before } : {}),
      },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One user: profile and contact, the microphone, the 30-day stats and the
// same facts a UTC day at a time (`days`, oldest first, every day present).
export const getUser = async ({ userId }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/users/${userId}`)
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// One user's day (`YYYY-MM-DD`, on their own clock) at the minute: each recorded minute,
// whether it arrived and what the router heard in it (`route`: speech found;
// `skip`: none); every moment the selector found and its outcome; the moments
// cut from the day and where each stands; and the day's comments, friends
// added and moment pushes, in order.
export const getUserDay = async ({ userId, day }) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/users/${userId}/day`, {
      params: { day },
    })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// What streaming spent per UTC day and per task, and the ElevenLabs pool this
// cycle (the Cost page). Read off the fleet's one row a day and the cycle
// rows only, so it costs the same at any cohort size; cached 60s server-side.
// `days` is 1 to 90. A day before the router meter has `usd.route` null
// (unmetered, never $0), and a day with no row at all has `books: 'none'`.
export const getCost = async ({ days } = {}) => {
  try {
    const { data } = await axios.get(`${BASE_URL}/cost`, { params: { days } })
    return data
  } catch (err) {
    throw new Error(err.response?.data?.detail || err.message)
  }
}

// A refusal that names each field it refused (422) reads as one line.
const detailOf = (err) => {
  const detail = err.response?.data?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail))
    return Object.entries(detail)
      .map(([field, why]) => `${field}: ${why}`)
      .join(' · ')
  return detail || err.message
}

// The cost lab (reclip-api docs/audio-streaming/cost-lab.md): every spend
// lever priced over the last whole UTC days, from the job that runs every 6
// hours. `ready` is false before its first run; `as_of` is when it ran.
export const getLab = async () => {
  try {
    const { data } = await axios.get(`${BASE_URL}/lab`)
    return data
  } catch (err) {
    throw new Error(detailOf(err))
  }
}

// What a plan of levers ({lever key: a setting on its curve}) would have done,
// replayed on the job's sample. A setting that is not on a lever's curve is
// refused, by lever.
export const replayLab = async (plan) => {
  try {
    const { data } = await axios.post(`${BASE_URL}/lab/replay`, { plan })
    return data
  } catch (err) {
    throw new Error(detailOf(err))
  }
}
